import {useEffect, useState} from "react";
import {getFromApi, postToApi, patchToApi, deleteFromApi} from "../api/auth";
import ConfirmButton from "../components/shared/ConfirmButton";

function domain(url) {
    try { return new URL(url).hostname.replace(/^www\./, ""); }
    catch { return url; }
}

function Favicon({url}) {
    const d = domain(url);
    return (
        <img
            src={`https://www.google.com/s2/favicons?domain=${d}&sz=32`}
            alt=""
            width={16} height={16}
            style={{borderRadius:2, flexShrink:0}}
            onError={e => { e.target.style.display = "none"; }}
        />
    );
}

export default function Bookmarks() {
    const [bookmarks, setBookmarks] = useState([]);
    const [url, setUrl]     = useState("");
    const [tag, setTag]     = useState("");
    const [title, setTitle] = useState("");
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const [editId, setEditId]   = useState(null);
    const [editTag, setEditTag] = useState("");
    const [editTitle, setEditTitle] = useState("");

    const load = async () => {
        try {
            const res = await getFromApi("api/bookmarks");
            setBookmarks(res.data || []);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const handleAdd = async (e) => {
        e.preventDefault();
        await postToApi("api/bookmarks", {name: url, tag, title});
        setUrl(""); setTag(""); setTitle("");
        load();
    };

    const handleEdit = async (id) => {
        await patchToApi(`api/bookmarks/${id}`, {tag: editTag, title: editTitle});
        setEditId(null);
        load();
    };

    const handleDelete = async (id) => {
        await deleteFromApi(`api/bookmarks/${id}`);
        load();
    };

    const tags = [...new Set(bookmarks.map(b => b.tag).filter(Boolean))];

    const filtered = bookmarks.filter(b => {
        const matchTag    = !filter || b.tag === filter;
        const matchSearch = !search  || b.name.toLowerCase().includes(search.toLowerCase()) || (b.title || "").toLowerCase().includes(search.toLowerCase());
        return matchTag && matchSearch;
    });

    return (
        <div>
            <div className="page-header">
                <h4><i className="bi bi-bookmark me-2 text-primary"/>Bookmarks</h4>
            </div>

            <div className="panel mb-4">
                <form onSubmit={handleAdd}>
                    <div className="row g-2 mb-2">
                        <div className="col-md-6">
                            <label className="form-label small fw-medium">URL</label>
                            <input className="form-control" placeholder="https://..." value={url} onChange={e => setUrl(e.target.value)} required/>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label small fw-medium">Title (optional)</label>
                            <input className="form-control" placeholder="My site" value={title} onChange={e => setTitle(e.target.value)}/>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label small fw-medium">Tag</label>
                            <input className="form-control" placeholder="work, design…" value={tag} onChange={e => setTag(e.target.value)}/>
                        </div>
                    </div>
                    <button type="submit" className="btn btn-dark btn-sm">
                        <i className="bi bi-plus-lg me-1"/>Add bookmark
                    </button>
                </form>
            </div>

            <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
                <input className="form-control form-control-sm" style={{maxWidth:220}} placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)}/>
                {tags.length > 0 && (
                    <>
                        <button className={`btn btn-sm ${!filter?"btn-dark":"btn-outline-secondary"}`} onClick={() => setFilter("")}>All</button>
                        {tags.map(t => (
                            <button key={t} className={`btn btn-sm ${filter===t?"btn-dark":"btn-outline-secondary"}`} onClick={() => setFilter(t)}>{t}</button>
                        ))}
                    </>
                )}
            </div>

            {loading ? <div className="text-muted text-center py-4">Loading...</div> : (
                <div className="row g-3">
                    {filtered.map(b => (
                        <div key={b._id} className="col-md-6 col-lg-4">
                            {editId === b._id ? (
                                <div className="panel h-100" style={{padding:"1rem"}}>
                                    <div className="mb-2">
                                        <label className="form-label small fw-medium">Title</label>
                                        <input className="form-control form-control-sm" value={editTitle} onChange={e => setEditTitle(e.target.value)}/>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label small fw-medium">Tag</label>
                                        <input className="form-control form-control-sm" value={editTag} onChange={e => setEditTag(e.target.value)}/>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <button className="btn btn-sm btn-dark" onClick={() => handleEdit(b._id)}>Save</button>
                                        <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditId(null)}>Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                <div className="panel h-100 d-flex flex-column" style={{padding:"1rem"}}>
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                        <Favicon url={b.name}/>
                                        <span className="small text-muted text-truncate">{domain(b.name)}</span>
                                        {b.tag && <span className="badge bg-secondary ms-auto">{b.tag}</span>}
                                    </div>
                                    <a href={b.name} target="_blank" rel="noopener noreferrer" className="fw-medium text-decoration-none mb-auto" style={{wordBreak:"break-word"}}>
                                        {b.title || domain(b.name)}
                                    </a>
                                    <div className="d-flex gap-1 mt-2 justify-content-end">
                                        <button className="btn btn-sm btn-outline-secondary" onClick={() => { setEditId(b._id); setEditTag(b.tag||""); setEditTitle(b.title||""); }}>
                                            <i className="bi bi-pencil"/>
                                        </button>
                                        <ConfirmButton className="btn btn-sm btn-outline-danger" onConfirm={() => handleDelete(b._id)}>
                                            <i className="bi bi-trash"/>
                                        </ConfirmButton>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                    {!filtered.length && (
                        <div className="empty-state">
                            <i className="bi bi-bookmark-plus"/>
                            <div>{search||filter ? "No bookmarks match." : "No bookmarks yet."}</div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
